import { Component, OnInit } from '@angular/core';
import { YCModalComponent } from '@yourcause/common/modals';
import { GdprService } from '../gdpr.service';
import { TypeSafeFormBuilder, TypeSafeFormGroup } from '@yourcause/common/core-forms';
import { Validators } from '@angular/forms';
import { ApplicationForGdpr, ApplicationsByEmail } from '../typings/gdpr.typing';
import { EmailExtensionValidator, IsAny, MustBeTrueOrFalse } from '@yourcause/common/form-control-validation';
import { LocationService } from '@core/services/location.service';
import { ProgramTypes } from '@core/typings/program.typing';
import { BulkOptions } from '@yourcause/common';
import { SpinnerService } from '@core/services/spinner.service';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { I18nService } from '@yourcause/common/i18n';

interface EmailFormGroup {
  email: string;
}
export class DeleteApplication {
  @IsAny()
  applicationId: number;

  @IsAny()
  organizationName: string;

  @IsAny()
  primaryApplicant: boolean;

  @MustBeTrueOrFalse(
    'common:textMustHaveNoPendingPayments',
    'Must have no pending payments',
    false,
    'hasPaymentsPending'
  )()
  hasPaymentsPending: boolean;
}

@Component({
  selector: 'gc-request-to-be-forgotten-modal',
  templateUrl: './request-to-be-forgotten-modal.component.html',
  styleUrls: ['./request-to-be-forgotten-modal.component.scss']
})
export class RequestToBeForgottenModalComponent extends YCModalComponent<{
  applicationIds: number[];
  email: string;
}> implements OnInit {
  formGroup: TypeSafeFormGroup<EmailFormGroup>;
  applicantEmailResponse: ApplicationsByEmail;
  applications: ApplicationForGdpr[];
  selectedApplicant: {
    name: string;
    initials: string;
    email: string;
  };
  bulkOptions: BulkOptions<ApplicationForGdpr> = {
    recordIdentifierAttribute: 'applicationId',
    onlyForTrackingRows: true,
    retainBulkSelectedItems: true,
    bulkActions: [{
      ValidationClass: DeleteApplication,
      label: '',
      exec: () => {}
    }]
  };
  bulkSelected: ApplicationForGdpr[] = [];
  didSearch = false;
  popover: PopoverDirective;
  desiredDeleteText = this.i18n.translate('common:btnDelete', {}, 'Delete').toUpperCase();
  currentDeleteText: string;
  helpLink = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-grant-managers-applicants-gdpr.html';
  hasBulkErrors: boolean;
  isValid = false;
  invalidApplicantEmail = false;

  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private gdprService: GdprService,
    private locationService: LocationService,
    private spinnerService: SpinnerService,
    private i18n: I18nService
  ) {
    super();
  }

  ngOnInit () {
    this.formGroup = this.formBuilder.group({
      email: ['', [Validators.required, EmailExtensionValidator]]
    });
  }

  async searchByEmail () {
    this.spinnerService.startSpinner();
    this.applicantEmailResponse = await this.gdprService.getApplicationsByEmail(this.formGroup.value.email);
    this.applications = this.applicantEmailResponse?.applications ?? [];
    this.spinnerService.stopSpinner();
    this.invalidApplicantEmail = false;
    this.didSearch = true;
    this.bulkSelected = [];
    if (this.applications.length > 0) {
      const firstName = this.applicantEmailResponse.applicantFirstName;
      const lastName = this.applicantEmailResponse.applicantLastName;
      const applicantName = `${firstName} ${lastName}`;
      this.selectedApplicant = {
        name: applicantName,
        initials: firstName[0] + lastName[0],
        email: this.formGroup.value.email
      };
    } else {
      this.selectedApplicant = null;
      if (this.applicantEmailResponse?.invalidApplicant) {
        this.invalidApplicantEmail = true;
      } else {
        // If no apps are found and the applicant is valid we still run the process behind the scenes
        // So any deleted apps for this user are obfuscated
        this.gdprService.handleAppsToBeForgotten([], this.formGroup.value.email, true, true);
      }
    }
  }

  emailChanged () {
    this.didSearch = false;
    this.invalidApplicantEmail = false;
  }

  onClearApplicant () {
    this.selectedApplicant = null;
    this.formGroup.get('email').setValue('');
    this.bulkSelected = [];
    this.hasBulkErrors = false;
    this.currentDeleteText = '';
    this.didSearch = false;
    this.invalidApplicantEmail = false;
  }

  bulkSelectedChanged (apps: ApplicationForGdpr[]) {
    this.bulkSelected = apps;
    this.setIsValid();
  }

  onHasBulkErrorsChange (hasBulkErrors: boolean) {
    this.hasBulkErrors = hasBulkErrors;
    if (hasBulkErrors) {
      this.currentDeleteText = '';
    }
    this.setIsValid();
  }

  setIsValid () {
    this.isValid = !this.hasBulkErrors &&
      this.bulkSelected.length > 0 &&
      this.desiredDeleteText === this.currentDeleteText;
  }

  navigateToApp (app: ApplicationForGdpr) {
    this.locationService.navigateToApp(
      {
        application: {
          id: app.applicationId
        },
        grantProgram: {
          programType: app.isNomination ? ProgramTypes.NOMINATION : ProgramTypes.GRANT
        }
      },
      'application'
    );
  }

  onPopoverReady (
    popover: PopoverDirective
  ) {
    if (!!popover) {
      this.popover = popover;
    }
  }

  proceedWithRemove () {
    this.popover?.hide();
    this.closeModal.emit({
      applicationIds: this.bulkSelected.map((app) => app.applicationId),
      email: this.selectedApplicant.email
    });
  }
}
